import React, { useEffect } from "react";
import { Alert, Container } from "../../../components";
import { useQueryParam } from "../../../components/hooks";
import { Heading, Button } from "@telia/teddy";
import { connect, useDispatch } from "react-redux";
import { FORM_ID, GTM_ECOMMERCE_REFILL_STEP, PAYMENT_METHOD, REFILL_LABELS } from "../../../../../constants";
import PAGES from "../../../../../constants/pages";
import { RootState } from "../../../../../models";
import { PaymentSelected } from "../../../../../models/state";
import { setNegativeFlash } from "../../../app/state/generalActions";
import { getErrorMessage } from "../../../app/state/selectors";
import BackLink from "../../../checkout/components/atoms/BackLink/BackLink";
import PaymentCard from "../../../checkout/components/organisms/PaymentCard/PaymentCard";
import { usePrevious } from "../../../checkout/hooks";
import { checkoutPayment } from "../../../checkout/state/actions/paymentActions";
import { getPaymentSelected, getPaymentSuccessRedirectUrl } from "../../../checkout/state/selectors/paymentSelectors";
import { trackFunnelEvent, trackRefillCheckoutAndNavigate } from "../../../google-analytics";
import { isLoading as isLoadingSelector } from "../../../loading/state/selectors";
import { getFormattedPhoneNumber } from "../../../rapidform/formValidation";
import RefillPrepaidEmailForm from "../../components/organisms/RefillPrepaidEmailForm/RefillPrepaidEmailForm";
import { setCustomerEmail, submitRefillOrder } from "../../state/actions/refillActions";
import {
    getRefillOrderRef,
    isOrderSubmitted,
    prepaidRefillTotalUpfrontPrice,
    selectedPackageNames,
    selectedPhonenumber,
} from "../../state/selectors";
import TEXT from "../../text-consts";
import "./Summary.less";

interface StateProps {
    isLoading: boolean;
    isSubmitted: boolean;
    logicalErrorMessage?: string;
    paymentSuccessRedirectUrl: string;
    refillOrderRef: string;
    selectedNames: string[];
    selectedNumber: string;
    selectedPaymentMethod: Partial<PaymentSelected>;
    totalUpfrontPrice: number;
}

type PrepaidRefillProps = StateProps;

const Summary: React.FC<PrepaidRefillProps> = ({
    selectedNames,
    selectedNumber,
    paymentSuccessRedirectUrl,
    selectedPaymentMethod,
    isLoading,
    refillOrderRef,
    isSubmitted,
    logicalErrorMessage,
    totalUpfrontPrice,
}) => {
    const packageNames = selectedNames.join(", ");
    const dispatch = useDispatch();
    const isPaymentFailed = useQueryParam("paymentFailed");
    const isSubmitFailed = useQueryParam("submitFailed");

    // Trigger GA funnel
    useEffect(() => {
        trackFunnelEvent({ step: "3", label: REFILL_LABELS[2], name: "Refill - Påfyll kontantkort" });
    }, []);

    useEffect(() => {
        // Check if paymentFailed/submitFailed (kept persistent since other requests would otherwise clear it on dispatch)

        if (isSubmitted) {
            window.location.assign(`${PAGES.PREPAID.REFILL.RECEIPT.URL}${refillOrderRef}/`);
        }

        if (isPaymentFailed) {
            dispatch(setNegativeFlash(TEXT.REFILL.ERROR.PAYMENT, undefined, true));
        }
        if (isSubmitFailed) {
            dispatch(setNegativeFlash(TEXT.REFILL.ERROR.SUBMIT, undefined, true));
        }
    }, [isPaymentFailed, isSubmitFailed, setNegativeFlash, isSubmitted]);

    const prevPaymentSuccessRedirectUrl = usePrevious(paymentSuccessRedirectUrl);
    useEffect(() => {
        if (paymentSuccessRedirectUrl && paymentSuccessRedirectUrl !== prevPaymentSuccessRedirectUrl) {
            dispatch(trackRefillCheckoutAndNavigate(GTM_ECOMMERCE_REFILL_STEP.SUMMARY, paymentSuccessRedirectUrl));
        }
    }, [paymentSuccessRedirectUrl, prevPaymentSuccessRedirectUrl, trackRefillCheckoutAndNavigate]);

    const onSubmit = (): void => {
        dispatch(checkoutPayment(undefined, undefined, undefined, selectedPaymentMethod.type));
    };

    const trySubmitAgain = (): void => {
        dispatch(submitRefillOrder());
    };

    const shouldShowPaymentButton = selectedPaymentMethod.type !== PAYMENT_METHOD.ADYEN_CC;

    return (
        <div className="prepaid-refill-summary">
            <div className="prepaid-refill-summary__heading-container">
                <div className="prepaid-refill-summary__heading-inner">
                    <div className="prepaid-refill-summary__heading-wrapper">
                        <Heading mb="100" as="h1" variant="title-600" className="prepaid-refill-summary__heading">
                            {TEXT.REFILL.SUMMARY.HEADING}
                        </Heading>
                    </div>
                </div>
            </div>
            {logicalErrorMessage && (
                <Container size="small">
                    <Alert kind="warning">{logicalErrorMessage}</Alert>
                </Container>
            )}
            <div className="prepaid-refill-summary__container">
                {selectedNumber && (
                    <div className="prepaid-refill-summary__selectedNumber">
                        <div className="prepaid-refill-summary__selectedNumber-content-with-border">
                            <div>Mobilnummer:</div>
                            <div data-di-mask>{getFormattedPhoneNumber(selectedNumber)}</div>
                        </div>
                        <div className="prepaid-refill-summary__selectedNumber-content-with-border">
                            <div>Produkt:</div>
                            <div>{packageNames}</div>
                        </div>
                        <div className="prepaid-refill-summary__selectedNumber-content">
                            <div>Totalpris:</div>
                            <div>{totalUpfrontPrice},-</div>
                        </div>
                    </div>
                )}
            </div>
            <div className="prepaid-refill-summary__email-container">
                <RefillPrepaidEmailForm onSubmitDetails={() => dispatch(setCustomerEmail())} />
                <Button variant="secondary" disabled={isLoading} type="submit" size="sm" form={FORM_ID.REFILL_PREPAID}>
                    {TEXT.REFILL.BUTTON.SUBMIT_EMAIL}
                </Button>
            </div>

            <div className="prepaid-refill-summary__container">
                <PaymentCard hideVoucher={true} />
            </div>
            <div className="prepaid-refill-summary__container">
                <div className="prepaid-refill-summary__buttonAndLink">
                    <BackLink text="Tilbake" />
                    {!isSubmitFailed && shouldShowPaymentButton && (
                        <Button variant="primary" onClick={onSubmit} disabled={isLoading} loading={isLoading}>
                            Gå til betaling
                        </Button>
                    )}
                    {isSubmitFailed && (
                        <Button variant="primary" onClick={trySubmitAgain} disabled={isLoading} loading={isLoading}>
                            Prøv å fullføre på nytt
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    selectedNumber: selectedPhonenumber(state),
    selectedNames: selectedPackageNames(state),
    totalUpfrontPrice: prepaidRefillTotalUpfrontPrice(state),
    paymentSuccessRedirectUrl: getPaymentSuccessRedirectUrl(state),
    refillOrderRef: getRefillOrderRef(state),
    selectedPaymentMethod: getPaymentSelected(state) || {},
    isSubmitted: isOrderSubmitted(state),
    isLoading: isLoadingSelector(state),
    logicalErrorMessage: getErrorMessage(state),
});

export default connect(mapStateToProps)(Summary);
